import { useEffect, useState, type ReactNode } from 'react';
import { downloadCSV, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { toCsv, type CsvColumn } from '../../shared/csvExport';
import {
  ACTION_CSV,
  CLICK_CSV,
  INTERACTION_CSV,
  TIMELINE_CSV,
  TIMELINE_ERROR,
  TIMELINE_PICK_USER,
  clickName,
  elementRoleLabel,
  fetchUsageTimeline,
  kindLabel,
  methodLabel,
  momentText,
  msText,
  timelineWhat,
  type TraceQuery,
  type UsageActionStat,
  type UsageClickStat,
  type UsageInteractionStat,
  type UsageTimeline,
} from './usageTrace';

/**
 * Widoki pełnego śladu działań na ekranie „Pomiar użycia” (decyzja
 * użytkownika 23.10.2026): „Działania” (najczęstsze, z kodami odpowiedzi),
 * „Kliknięcia” z etykietami i pozostałe interakcje, „Oś czasu konta” —
 * kto, co i kiedy, po kolei. Każdy z eksportem CSV. Ekran i tak widzi
 * wyłącznie rola ADMIN (strażnik w API i w `UsageAnalytics`).
 */

type RouteLabel = (route: string | null) => string;

const exportCsv = (rows: readonly object[], columns: readonly CsvColumn[], name: string) =>
  downloadCSV(toCsv(rows as unknown as RaRecord[], columns), name);

const Header = ({ title, children }: { title: string; children?: ReactNode }) => (
  <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 0.5 }}>
    <Typography variant="h6">{title}</Typography>
    {children}
  </Stack>
);

const ExportButton = ({ onClick }: { onClick: () => void }) => (
  <Button size="small" startIcon={<DownloadIcon />} onClick={onClick} data-testid="usage-export-csv">
    Eksport CSV
  </Button>
);

const Empty = ({ text }: { text: string }) => (
  <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
    {text}
  </Typography>
);

export const ActionsView = ({
  actions,
  routeLabel,
  suffix,
}: {
  actions: UsageActionStat[];
  routeLabel: RouteLabel;
  suffix: string;
}) => (
  <Box data-testid="usage-actions">
    <Header title="Działania zmieniające dane">
      <ExportButton onClick={() => exportCsv(actions, ACTION_CSV(routeLabel), `pomiar-dzialania_${suffix}`)} />
    </Header>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      Każde zapisanie, zmiana, usunięcie i czynność wysłana z ekranu: rodzaj zapytania, kod odpowiedzi i czas. Bez
      treści tego, co zapisano.
    </Typography>
    {actions.length === 0 ? (
      <Empty text="Brak działań w tym okresie." />
    ) : (
      <Table size="small" aria-label="Działania">
        <TableHead>
          <TableRow>
            <TableCell>Działanie</TableCell>
            <TableCell>Zapytanie</TableCell>
            <TableCell align="right">Liczba</TableCell>
            <TableCell align="right">Osoby</TableCell>
            <TableCell>Kody odpowiedzi</TableCell>
            <TableCell align="right">Mediana czasu</TableCell>
            <TableCell>Najczęściej z ekranu</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {actions.map((a) => (
            <TableRow key={`${a.method} ${a.apiPattern}`} data-testid="usage-action-row">
              <TableCell>{methodLabel(a.method)}</TableCell>
              <TableCell sx={{ wordBreak: 'break-all' }}>{a.apiPattern}</TableCell>
              <TableCell align="right">{a.count}</TableCell>
              <TableCell align="right">{a.users}</TableCell>
              <TableCell>
                {a.statusCodes.map((c) => (
                  <Chip
                    key={String(c.statusCode)}
                    size="small"
                    variant="outlined"
                    color={c.statusCode === null || c.statusCode >= 500 ? 'error' : c.statusCode >= 400 ? 'warning' : 'success'}
                    label={`${c.statusCode ?? 'bez odpowiedzi'} × ${c.count}`}
                    sx={{ mr: 0.5, mb: 0.25 }}
                  />
                ))}
              </TableCell>
              <TableCell align="right">{msText(a.medianDurationMs)}</TableCell>
              <TableCell>{routeLabel(a.route)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </Box>
);

export const ClicksView = ({
  clicks,
  interactions,
  routeLabel,
  suffix,
}: {
  clicks: UsageClickStat[];
  interactions: UsageInteractionStat[];
  routeLabel: RouteLabel;
  suffix: string;
}) => (
  <Box data-testid="usage-clicks" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: '3fr 2fr' } }}>
    <Box>
      <Header title="Kliknięcia">
        <ExportButton onClick={() => exportCsv(clicks, CLICK_CSV(routeLabel), `pomiar-klikniecia_${suffix}`)} />
      </Header>
      {clicks.length === 0 ? (
        <Empty text="Brak kliknięć w tym okresie." />
      ) : (
        <Table size="small" aria-label="Kliknięcia">
          <TableHead>
            <TableRow>
              <TableCell>Element</TableCell>
              <TableCell>Rodzaj</TableCell>
              <TableCell>Sekcja</TableCell>
              <TableCell>Najczęściej na ekranie</TableCell>
              <TableCell align="right">Kliknięcia</TableCell>
              <TableCell align="right">Osoby</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clicks.map((c, i) => (
              <TableRow key={`${c.kind}-${c.elementId}-${c.label}-${c.section}-${i}`} data-testid="usage-click-row">
                <TableCell sx={{ wordBreak: 'break-word' }}>
                  {clickName(c)}
                  {c.label && c.elementId ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                      {c.elementId}
                    </Typography>
                  ) : null}
                  {c.contract ? <Chip size="small" label="z kontraktu" color="info" variant="outlined" sx={{ ml: 0.5 }} /> : null}
                </TableCell>
                <TableCell>
                  {elementRoleLabel(c.elementRole)}
                  {c.kind !== 'click' ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {kindLabel(c.kind)}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{c.section ?? '—'}</TableCell>
                <TableCell>{routeLabel(c.route)}</TableCell>
                <TableCell align="right">{c.count}</TableCell>
                <TableCell align="right">{c.users}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
    <Box>
      <Header title="Okna, filtry, strony list, wylogowania">
        <ExportButton onClick={() => exportCsv(interactions, INTERACTION_CSV, `pomiar-interakcje_${suffix}`)} />
      </Header>
      {interactions.length === 0 ? (
        <Empty text="Brak w tym okresie." />
      ) : (
        <Table size="small" aria-label="Okna, filtry, strony list">
          <TableHead>
            <TableRow>
              <TableCell>Rodzaj</TableCell>
              <TableCell>Nazwa</TableCell>
              <TableCell align="right">Liczba</TableCell>
              <TableCell align="right">Osoby</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {interactions.map((x, i) => (
              <TableRow key={`${x.kind}-${x.label}-${i}`} data-testid="usage-interaction-row">
                <TableCell>{kindLabel(x.kind)}</TableCell>
                <TableCell>
                  {x.label ?? '—'}
                  {x.medianDurationSec !== null ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      mediana: {msText(x.medianDurationSec * 1000)}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right">{x.count}</TableCell>
                <TableCell align="right">{x.users}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Typography variant="caption" color="text.secondary">
        Przy filtrach zapisujemy samą nazwę filtru, nigdy wpisaną wartość.
      </Typography>
    </Box>
  </Box>
);

/** Oś czasu jednego konta — kto, co i kiedy, po kolei (tylko ADMIN). */
export const TimelineView = ({ query, routeLabel }: { query: TraceQuery; routeLabel: RouteLabel }) => {
  const [data, setData] = useState<UsageTimeline | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { userId, from, to, environment, impersonation } = query;

  useEffect(() => {
    if (!userId) {
      setData(null);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    fetchUsageTimeline({ from, to, environment, userId, impersonation })
      .then((t) => {
        if (!alive) return;
        setData(t);
        setError(null);
      })
      .catch(() => {
        if (alive) setError(TIMELINE_ERROR);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [userId, from, to, environment, impersonation]);

  if (!userId) {
    return (
      <Box data-testid="usage-timeline">
        <Alert severity="info">{TIMELINE_PICK_USER}</Alert>
      </Box>
    );
  }
  return (
    <Box data-testid="usage-timeline">
      <Header title={data ? `Oś czasu konta: ${data.user.username ?? `nr ${data.user.id}`}` : 'Oś czasu konta'}>
        {data ? (
          <ExportButton
            onClick={() => exportCsv(data.events, TIMELINE_CSV(routeLabel), `pomiar-os-czasu_${userId}_${from}_${to}`)}
          />
        ) : null}
      </Header>
      {loading ? <CircularProgress size={22} /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      {data?.truncated ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Pokazujemy pierwsze {data.limit.toLocaleString('pl-PL')} zdarzeń z okresu — całość daje „Eksport wszystkich
          zdarzeń (CSV)”. Zawęź okres.
        </Alert>
      ) : null}
      {data && data.events.length === 0 ? <Empty text="To konto nie ma zdarzeń w tym okresie." /> : null}
      {data && data.events.length > 0 ? (
        <Table size="small" aria-label="Oś czasu konta">
          <TableHead>
            <TableRow>
              <TableCell>Kiedy</TableCell>
              <TableCell>Rodzaj</TableCell>
              <TableCell>Ekran</TableCell>
              <TableCell>Co</TableCell>
              <TableCell>Rekord</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.events.map((e) => (
              <TableRow key={e.id} data-testid={`usage-timeline-row-${e.kind}`}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{momentText(e.occurredAt)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={kindLabel(e.kind)}
                    color={
                      e.kind === 'action'
                        ? e.statusCode !== null && e.statusCode < 400
                          ? 'success'
                          : 'warning'
                        : e.kind === 'apiError'
                          ? 'warning'
                          : 'default'
                    }
                  />
                </TableCell>
                <TableCell>{routeLabel(e.route)}</TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{timelineWhat(e)}</TableCell>
                <TableCell>
                  {e.recordId ? `${e.recordType === 'preOrder' ? 'prezlecenie' : 'zlecenie'} nr ${e.recordId}` : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </Box>
  );
};
